import type {
  CalculatedPlantingWindow,
  SeedStartingCalculation,
} from '@/lib/seedStartingCalculator';
import {
  formatPlantingReadinessStatus,
  type PlantingReadinessResult,
} from '@/lib/plantingReadiness';

const DATE_ROWS: Array<{
  key: 'indoorStart' | 'transplant' | 'directSow';
  label: string;
}> = [
  { key: 'indoorStart', label: 'Start Indoors' },
  { key: 'transplant', label: 'Transplant Outdoors' },
  { key: 'directSow', label: 'Direct Sow' },
];

export function hasSeedStartingDateRows(calculation: SeedStartingCalculation): boolean {
  return DATE_ROWS.some(row => {
    const window = calculation[row.key];
    return window && (calculation.lastFrostDate || window.status !== 'needs-zone');
  });
}

function labelForRow(
  key: 'indoorStart' | 'transplant' | 'directSow',
  defaultLabel: string,
  calculation: SeedStartingCalculation,
): string {
  if (key === 'transplant' && calculation.mode === 'transplant-only') {
    return 'Best Planting Window';
  }
  if (key === 'directSow' && calculation.mode === 'direct-sow') {
    return 'Direct Sow Window';
  }
  return defaultLabel;
}

function DateRow({
  label,
  window,
  showSource,
}: {
  label: string;
  window: CalculatedPlantingWindow;
  showSource: boolean;
}) {
  return (
    <div className="rounded-lg border border-sand bg-white px-4 py-3">
      <p className="font-body text-[11px] text-sage uppercase tracking-wider font-semibold">
        {label}
      </p>
      {window.label ? (
        <p className="font-body text-[16px] text-dark-text font-semibold mt-1">
          {window.label}
        </p>
      ) : (
        <p className="font-body text-[14px] text-dark-text mt-1 leading-snug">
          {window.notes[0]}
        </p>
      )}
      {window.label && window.notes.length > 0 && (
        <p className="font-body text-[13px] text-sage mt-1 leading-snug">
          {window.notes.join(' ')}
        </p>
      )}
      {showSource && (
        <p className="font-body text-[12px] text-sage mt-2 leading-snug">
          Based on: {window.sourceText}
        </p>
      )}
    </div>
  );
}

function readinessTone(status: PlantingReadinessResult['status']): string {
  if (status === 'ready') return 'border-[#C5D9B8] bg-light-sage text-deep-green';
  if (status === 'wait') return 'border-[#D9B8A8] bg-[#FFF8F1] text-[#8A4B2A]';
  if (status === 'caution') return 'border-[#D8C7A1] bg-[#FFFDF2] text-[#7A6525]';
  return 'border-sand bg-cream text-sage';
}

function WeatherReadinessCard({
  readiness,
  compact,
}: {
  readiness?: PlantingReadinessResult | null;
  compact: boolean;
}) {
  if (!readiness) return null;

  return (
    <section className={[
      'rounded-xl border px-4 py-3',
      readinessTone(readiness.status),
    ].join(' ')}>
      <div className={compact ? 'space-y-1.5' : 'space-y-2'}>
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-body text-[11px] uppercase tracking-wider font-semibold opacity-80">
            Current Readiness
          </span>
          <span className="rounded-full bg-white/70 px-2 py-0.5 font-body text-[12px] font-semibold">
            {formatPlantingReadinessStatus(readiness.status)}
          </span>
        </div>
        <p className="font-body text-[14px] text-dark-text leading-snug">
          {readiness.action}
        </p>
        {!compact && (
          <p className="font-body text-[13px] text-sage leading-snug">
            {readiness.dateGuidance} {readiness.weatherGuidance}
          </p>
        )}
      </div>
    </section>
  );
}

function TransplantGuidanceResults({
  calculation,
  compact,
}: {
  calculation: SeedStartingCalculation;
  compact: boolean;
}) {
  const guidance = calculation.transplantGuidance;
  if (!guidance) {
    return (
      <div className={compact ? 'space-y-3' : 'space-y-4'}>
        {!compact && (
          <div className="rounded-xl border border-sand bg-cream px-5 py-4">
            <p className="font-body text-[11px] text-sage uppercase tracking-wider font-semibold">
              Calculator Mode
            </p>
            <p className="font-body text-[16px] text-dark-text font-semibold mt-1">
              {calculation.modeLabel}
            </p>
            <p className="font-body text-[14px] text-sage mt-1 leading-snug">
              {calculation.modeDescription}
            </p>
          </div>
        )}
      </div>
    );
  }

  const springTitle = calculation.zone
    ? `Spring window (Zone ${calculation.zone})`
    : 'Spring window';

  return (
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
      {!compact && (
        <div className="rounded-xl border border-sand bg-cream px-5 py-4">
          <p className="font-body text-[11px] text-sage uppercase tracking-wider font-semibold">
            Calculator Mode
          </p>
          <p className="font-body text-[16px] text-dark-text font-semibold mt-1">
            {calculation.modeLabel}
          </p>
          <p className="font-body text-[14px] text-sage mt-1 leading-snug">
            {calculation.modeDescription}
          </p>
        </div>
      )}

      <section className="rounded-xl border border-sand bg-white px-4 py-3">
        <p className="font-body text-[11px] text-sage uppercase tracking-wider font-semibold">
          Best Planting Window
        </p>
        <div className={compact ? 'mt-3 grid grid-cols-1 gap-2' : 'mt-3 grid grid-cols-1 md:grid-cols-2 gap-3'}>
          <div className="rounded-lg bg-light-sage px-4 py-3">
            <p className="font-body text-[11px] text-sage uppercase tracking-wider font-semibold">
              {springTitle}
            </p>
            <p className="font-body text-[15px] text-dark-text font-semibold mt-1">
              {guidance.springWindow.label}
            </p>
            <p className="font-body text-[13px] text-sage mt-1 leading-snug">
              {guidance.springWindow.detail}
            </p>
          </div>
          <div className="rounded-lg bg-light-sage px-4 py-3">
            <p className="font-body text-[11px] text-sage uppercase tracking-wider font-semibold">
              Autumn window
            </p>
            <p className="font-body text-[15px] text-dark-text font-semibold mt-1">
              {guidance.autumnWindow.label}
            </p>
            <p className="font-body text-[13px] text-sage mt-1 leading-snug">
              {guidance.autumnWindow.detail}
            </p>
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-sand bg-white px-4 py-3">
        <p className="font-body text-[11px] text-sage uppercase tracking-wider font-semibold">
          Planting Method
        </p>
        <p className="font-body text-[14px] text-dark-text mt-1 leading-snug">
          {guidance.plantingMethod}
        </p>
      </section>

      <section className="rounded-lg border border-sand bg-white px-4 py-3">
        <p className="font-body text-[11px] text-sage uppercase tracking-wider font-semibold">
          Critical Timing Note
        </p>
        <p className="font-body text-[14px] text-dark-text mt-1 leading-snug">
          {guidance.criticalTimingNote}
        </p>
      </section>
    </div>
  );
}

export default function SeedStartingDateResults({
  calculation,
  readiness,
  compact = false,
  showSource = false,
}: {
  calculation: SeedStartingCalculation;
  readiness?: PlantingReadinessResult | null;
  compact?: boolean;
  showSource?: boolean;
}) {
  if (calculation.mode === 'transplant-only') {
    return (
      <div className={compact ? 'space-y-3' : 'space-y-4'}>
        <TransplantGuidanceResults calculation={calculation} compact={compact} />
        <WeatherReadinessCard readiness={readiness} compact={compact} />
      </div>
    );
  }

  const rows = calculation.lastFrostDate
    ? DATE_ROWS
        .map(row => ({ ...row, window: calculation[row.key] }))
        .filter((row): row is typeof row & { window: CalculatedPlantingWindow } => Boolean(row.window))
    : DATE_ROWS
        .map(row => ({ ...row, window: calculation[row.key] }))
        .filter((row): row is typeof row & { window: CalculatedPlantingWindow } =>
          Boolean(row.window && row.window.status !== 'needs-zone'),
        );

  return (
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
      {!compact && (
        <div className="rounded-xl border border-sand bg-cream px-5 py-4">
          <p className="font-body text-[11px] text-sage uppercase tracking-wider font-semibold">
            Calculator Mode
          </p>
          <p className="font-body text-[16px] text-dark-text font-semibold mt-1">
            {calculation.modeLabel}
          </p>
          <p className="font-body text-[14px] text-sage mt-1 leading-snug">
            {calculation.modeDescription}
          </p>
        </div>
      )}

      <div className={compact ? 'rounded-lg bg-light-sage px-4 py-3' : 'rounded-xl bg-light-sage px-5 py-4'}>
        <p className="font-body text-[11px] text-sage uppercase tracking-wider font-semibold">
          Average Last Frost
        </p>
        {calculation.lastFrostLabel ? (
          <p className="font-body text-[15px] text-dark-text font-semibold mt-1">
            {calculation.lastFrostLabel}
            {calculation.zone ? ` in Zone ${calculation.zone}` : ''}
          </p>
        ) : (
          <p className="font-body text-[14px] text-sage mt-1 leading-snug">
            {calculation.zone
              ? `Zone ${calculation.zone} does not have a spring last-frost estimate in this MVP calculator yet.`
              : 'Set your growing zone to see personalized calendar dates.'}
          </p>
        )}
      </div>

      <WeatherReadinessCard readiness={readiness} compact={compact} />

      {rows.length > 0 && (
        <div className={compact ? 'grid grid-cols-1 gap-2' : 'grid grid-cols-1 md:grid-cols-3 gap-3'}>
          {rows.map(row => (
            <DateRow
              key={row.key}
              label={labelForRow(row.key, row.label, calculation)}
              window={row.window}
              showSource={showSource}
            />
          ))}
        </div>
      )}

      {calculation.propagationNote && (
        <div className="rounded-lg border border-sand bg-white px-4 py-3">
          <p className="font-body text-[11px] text-sage uppercase tracking-wider font-semibold">
            Planting Method
          </p>
          <p className="font-body text-[14px] text-dark-text mt-1 leading-snug">
            {calculation.propagationNote}
          </p>
        </div>
      )}
    </div>
  );
}
